import { App, UnfallVorbelegungdaten, Vorbelegungdaten } from '@ps20-frontend/frontend-models';
import * as bffenums from '@ps20/bff-models';
import {
  Produktlinienname,
  VertragEigenschaftHinweis,
  Zahlungsart,
  Zahlweise,
} from '@ps20/bff-models';

export interface Vorvertrag extends Vertrag {
  produktlinieAnzeigename: Produktlinienname;
  produktIds: App[];
  neugeschaeftSperren: NeugeschaeftSperren[];
  vorbelegungAktiv: boolean;
  umstellungszwang: boolean;
  tarifGenerationPS10?: boolean;
  tarifGenerationPS20?: boolean;
  beibehaltung: boolean;
  ruhend: boolean;
  firmenvertrag: boolean;
  vorbelegungsdaten?: UnfallVorbelegungdaten[];
  vnWechsel?: boolean;
  aenderungdatum?: string;
  vorvertragSofortschutzAblaufDatum?: string;
}

export interface StandaloneProdukt extends Vertrag {
  einschliessenDetails: EinschlussDetail[];
  einschliessenPflichtErrorText: string;
  einschliessenderVertrag: number[];
  firmenvertrag?: boolean;
}

export interface EinschlussDetail {
  appId: App;
  pflichtZusatzoptionen: string[];
}

export interface NeugeschaeftSperren {
  appId: App;
  neugeschaeftSperren: boolean;
}

export interface Vertrag {
  vertragsnummer: string;
  linkToSAbs: string;
  anzeigename: string;
  zusatzinformation: string;
  risikoortInfo: RisikoortInfo;
  spLaufnummer: number;
  zahlungsperiode: Zahlweise;
  zahlungsart: Zahlungsart;
  isAssekuranztarif: boolean;
  berechnetePraemieBrutto: number;
  jahresbestandpraemieNetto: number;
  jahresbestandpraemieBrutto: number;
  icon: string;
  infoText: string;
  ersetzen: boolean;
  eigenschaftHinweise: Hinweismeldung[];
  vorvertragSofortschutz: boolean;
  isStorniert: boolean;
  stornoDetail: bffenums.VertragStornoDetail;
  verkaufsangebotKurznamen?: string[];
  vorbelegungdaten: Vorbelegungdaten[];
  vertragskennzeichen: bffenums.Vorvertragskennzeichen;
  ablaufdatum?: string;
  hasMultipleVn?: boolean;
}

export interface Hinweismeldung {
  eigenschaftHinweis: VertragEigenschaftHinweis;
  meldung: string;
}

export interface RisikoortInfo {
  valid: boolean;
  anzeige: string;
}

export interface ZahlungsoptionenVorvertrag {
  zahlungsweise: Zahlweise;
  zahlungsart: Zahlungsart;
}

export function determineZahlungsoptionenChangedPayload(
  standardVorvertraege: Array<Vorvertrag>,
  standaloneVorvertraege: Array<StandaloneProdukt>,
  isMaklerInkasso: boolean,
): ZahlungsoptionenVorvertrag {
  const allZahlungsperioden: Array<string> = [];
  standardVorvertraege
    ?.filter(vertrag => !vertrag.isStorniert)
    .forEach(value => allZahlungsperioden.push(value.zahlungsperiode));
  standaloneVorvertraege
    ?.filter(vertrag => !vertrag.isStorniert)
    .forEach(value => allZahlungsperioden.push(value.zahlungsperiode));
  const zahlungsweise =
    allZahlungsperioden.length > 0 &&
    allZahlungsperioden.every(zp => zp === allZahlungsperioden[0])
      ? (Zahlweise as Record<string, Zahlweise>)[allZahlungsperioden[0].toUpperCase()]
      : Zahlweise.MONATLICH;
  let zahlungsart;
  if (isMaklerInkasso) {
    zahlungsart = Zahlungsart.MAKLER_INKASSO;
  } else {
    const allZahlungsart: Array<Zahlungsart> = [];
    standardVorvertraege
      ?.filter(vertrag => !vertrag.isStorniert)
      .forEach(value => allZahlungsart.push(value.zahlungsart));
    standaloneVorvertraege
      ?.filter(vertrag => !vertrag.isStorniert)
      .forEach(value => allZahlungsart.push(value.zahlungsart));
    zahlungsart = allZahlungsart.every(zp => zp === allZahlungsart[0])
      ? allZahlungsart[0]
      : Zahlungsart.LASTSCHRIFT;
  }
  return {
    zahlungsweise,
    zahlungsart:
      (zahlungsart === Zahlungsart.RECHNUNG && zahlungsweise === Zahlweise.JAEHRLICH) ||
      zahlungsart === Zahlungsart.MAKLER_INKASSO
        ? zahlungsart
        : Zahlungsart.LASTSCHRIFT,
  } as ZahlungsoptionenVorvertrag;
}

export function determineAssekuranztarif(
  standardVorvertraege: Array<Vorvertrag>,
  standaloneVorvertraege: Array<StandaloneProdukt>,
): boolean {
  const isAssekuranztarifInStandardVertrag = standardVorvertraege?.find(
    value => value.isAssekuranztarif,
  );
  const isAssekuranztarifInStandaloneVertrag = standaloneVorvertraege?.find(
    value => value.isAssekuranztarif,
  );

  return (
    isAssekuranztarifInStandaloneVertrag !== undefined ||
    isAssekuranztarifInStandardVertrag !== undefined
  );
}
